import { useMemo } from "react";

import { TableData } from "@/states/unitTableReducer/unitTableReducer";

/**
 * A hook to derive unit info for the remove modal. If we don't do it here then we have to prop drill the table data etc which is not ideal for testing and separation of concerns.
 */
export const useRemoveModalUnitInfo = ({
  cellId,
  customUnit,
  orderedColumns,
  tableData,
}: {
  cellId: string;
  customUnit?: {
    id: string;
    title: string;
    description?: string;
    startOnColumn: string;
    span?: number;
    cohort: string;
  };
  orderedColumns: { id: string; title: string; order: number }[];
  tableData: Pick<TableData, "cells" | "rows" | "columns" | "resources">;
}) => {
  // derive unit info for the remove modal. If we don't do it here then we have to prop drill the table data etc which is not ideal for testing and separation of concerns.
  const removeUnitModalInfo: {
    title: string;
    startOnColumn: string;
    endOnColumn: string;
    cohort: string;
  } = useMemo(() => {
    if (customUnit) {
      const startOnColumnLabel =
        orderedColumns.find((col) => col.id === customUnit.startOnColumn)
          ?.title || "";

      const endOnColumnLabel: string = orderedColumns.find(
        (col) => col.id === customUnit.startOnColumn
      )
        ? orderedColumns[
            Math.min(
              orderedColumns.length - 1,
              orderedColumns.findIndex(
                (col) => col.id === customUnit.startOnColumn
              ) +
                ((customUnit?.span || 1) - 1)
            )
          ].title
        : startOnColumnLabel; // if span is 1, end column is the same as start column

      const cohortLabel = tableData.rows.find(
        (row) => row.id === customUnit.cohort
      )?.title;

      return {
        title: customUnit.title || "",
        startOnColumn: startOnColumnLabel || "",
        endOnColumn: endOnColumnLabel || "",
        cohort: cohortLabel || "",
      };
    } else {
      const targetCell = tableData.cells.find((cell) => cell.id === cellId);

      if (!targetCell) {
        return {
          title: "Target cell not found",
          startOnColumn: "Start column not found",
          endOnColumn: "End column not found",
          cohort: "Cohort not found",
        };
      }

      const resource =
        "resourceId" in targetCell && targetCell.resourceId
          ? tableData.resources.find((res) => res.id === targetCell.resourceId)
          : undefined;

      const startOnColumnLabel =
        orderedColumns.find((col) => col.id === targetCell.column)?.title || "";

      const endOnColumnLabel: string = orderedColumns.find(
        (col) => col.id === targetCell.column
      )
        ? orderedColumns[
            Math.min(
              orderedColumns.length - 1,
              orderedColumns.findIndex((col) => col.id === targetCell.column) +
                ((targetCell?.span || 1) - 1)
            )
          ].title
        : startOnColumnLabel; // if span is 1, end column is the same as start column

      const cohortLabel = tableData.rows.find(
        (row) => row.id === targetCell.row
      )?.title;

      return {
        title: resource?.name || "Resource not found",
        startOnColumn: startOnColumnLabel,
        endOnColumn: endOnColumnLabel,
        cohort: cohortLabel || "",
      };
    }
  }, [
    cellId,
    customUnit,
    orderedColumns,
    tableData.cells,
    tableData.resources,
    tableData.rows,
  ]);

  return { removeUnitModalInfo };
};
